import { EventId } from '@/models/Event';
import { MessageId } from '@/models/Message';
import { ThreadId } from '@/models/Thread';

import { gql } from '@apollo/client';

export type StepId = string;
export type TaskId = string;

export enum StepRunState {
  CREATED = 'created',
  RUNNING = 'running',
  SUCCEEDED = 'succeeded',
  FAILED = 'failed',
}

export function optStepRunState(value: string | null): StepRunState | null {
  if (!value) {
    return null;
  }
  const step = value.toLowerCase();
  return (
    Object.values(StepRunState).find((enumValue) => enumValue === step) ?? null
  );
}

export function getStepRunState(value: string): StepRunState {
  const stepRunState = optStepRunState(value);
  if (!stepRunState) {
    throw new Error(`Invalid StepRunState: ${value}`);
  }
  return stepRunState;
}

export interface StepProgress {
  id: EventId;
  threadId: ThreadId;
  messageId: MessageId;
  stepId: StepId;
  taskId: TaskId;
  runState: StepRunState;
  shortDescription: string | null;
  longDescription: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  estFinishedAt: string | null;
  errorMessage: string | null;
  actorId: string | null;
}

export function stepProgressFactory(
  overrides: Partial<StepProgress> = {},
): StepProgress {
  return {
    id: '',
    threadId: '',
    messageId: '',
    stepId: '',
    taskId: '',
    runState: StepRunState.CREATED,
    shortDescription: null,
    longDescription: null,
    createdAt: null,
    startedAt: null,
    finishedAt: null,
    estFinishedAt: null,
    errorMessage: null,
    actorId: null,
    ...overrides,
  };
}

export interface StepProgressFromGraphQL {
  id: EventId;
  thread: { id: ThreadId };
  message: { id: MessageId };
  stepId: StepId;
  taskId: TaskId;
  runState: string;
  shortDescription: string | null;
  longDescription: string | null;
  createdAt: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  estFinishedAt: string | null;
  errorMessage: string | null;
  actorId: string | null;
}

export const GQL_STEP_PROGRESS_FRAGMENT_NAME = 'StepProgressFragment';
export const GQL_STEP_PROGRESS_FRAGMENT = gql`
  fragment ${GQL_STEP_PROGRESS_FRAGMENT_NAME} on StepProgress {
    id
    shortDescription
    longDescription
    stepId
    runState
    createdAt
    startedAt
    finishedAt
    estFinishedAt
    taskId
    errorMessage
    actorId
    thread {
      id
    }
    message {
      id
    }
  }
`;

export function getStepProgressFromGraphQL(
  raw: StepProgressFromGraphQL,
): StepProgress {
  const { thread, message, runState, ...rest } = raw;
  return {
    ...rest,
    runState: getStepRunState(runState),
    messageId: message.id,
    threadId: thread.id,
  };
}
